import React, { useState, useEffect } from 'react';
import {
  Input, Button, Table, Tag, Space, Card, Row, Col, Avatar, Tooltip,
  Select, DatePicker, Typography, Dropdown, Menu, message, Modal
} from 'antd';
import {
  SearchOutlined, EyeOutlined, UserOutlined, PhoneOutlined,
  EnvironmentOutlined, CarOutlined, ShopOutlined, MoreOutlined,
  ExportOutlined, ReloadOutlined, InboxOutlined, RollbackOutlined, DeleteOutlined
} from '@ant-design/icons';
import { UserAddOutlined } from '@ant-design/icons';
import moment from 'moment';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig, driversListUrl } from '../../services/ApiConfigs';
import { useSelector } from 'react-redux';
import { openMap } from '../../services/helper';
import { useNavigate } from 'react-router-dom';
import { getStatusConfig } from '../../utils/orderStatusUtils';
import MainLayout from '../../components/MainLayout';
import DashboardCards from './DashboardCards';
import AsignDriver from './AsignDriver';
import MultipleOrdersAssign from './MultipleOrdersAssign';
import SegmentedButton from './SegmentedButton';
import { HomeContext } from './HomeContext';
import styles from './Orders.module.css';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const Orders = () => {
  const navigate = useNavigate();
  const userDetail = useSelector((res) => res.UserData.userData.user);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [asignDriverModalOpen, setAsignDriverModalOpen] = useState(false);
  const [multipleModalOpen, setMultipleModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [activeButton, setActiveButton] = useState('pending_orders');
  const [archiveFilter, setArchiveFilter] = useState('active'); // 'active' | 'archived' | 'all'

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalOrders, setTotalOrders] = useState(0);
  const [counts, setCounts] = useState({});

  // Debounce search text
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Fetch warehouses and drivers once on mount
  useEffect(() => {
    fetchWarehouses();
    fetchDrivers();
  }, []);

  // Reset page to 1 on filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchText, statusFilter, dateRange, activeButton, archiveFilter]);

  // Fetch orders when page, pageSize, or filters change
  useEffect(() => {
    if (userDetail?.storeId) {
      fetchOrders();
    }
  }, [userDetail?.storeId, page, pageSize, archiveFilter, statusFilter, dateRange, activeButton, debouncedSearchText]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const storeId = userDetail?.storeId;
      if (!storeId) return;

      let url = `${ApiConfig.getallOrderByStoreId}/${storeId}?page=${page}&limit=${pageSize}`;
      if (archiveFilter === 'archived') url += '&archive=1';
      else if (archiveFilter === 'all') url += '&includeArchived=1';

      if (debouncedSearchText) {
        url += `&search=${encodeURIComponent(debouncedSearchText)}`;
      }

      if (statusFilter !== 'all') {
        url += `&status=${encodeURIComponent(statusFilter)}`;
      }

      if (activeButton) {
        url += `&tab=${encodeURIComponent(activeButton)}`;
      }

      if (dateRange && dateRange[0] && dateRange[1]) {
        url += `&startDate=${dateRange[0].format('YYYY-MM-DD')}&endDate=${dateRange[1].format('YYYY-MM-DD')}`;
      }

      const todayStart = moment().startOf('day').toISOString();
      const todayEnd = moment().endOf('day').toISOString();
      url += `&todayStart=${encodeURIComponent(todayStart)}&todayEnd=${encodeURIComponent(todayEnd)}`;

      const response = await ApiServices('get', url);
      if (response) {
        let ordersData = [];
        if (response.pagination) {
          ordersData = Array.isArray(response.data) ? response.data : [];
          setOrders(ordersData);
          setTotalOrders(response.pagination.total);
        } else {
          ordersData = Array.isArray(response.data) ? response.data : [];
          setOrders(ordersData);
          setTotalOrders(ordersData.length);
        }
        if (response.counts) {
          setCounts(response.counts);
        } else {
          // Fallback calculation for old backend/offline support
          const localCounts = {
            all: ordersData.length,
            today: ordersData.filter(o => moment(o.createdAt).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')).length,
            assigned: ordersData.filter(o => (o.shipments || []).some(s => s.driverId)).length,
            completed: ordersData.filter(o => ['DELIVERED', 'COMPLETED'].includes(o.orderStatus)).length
          };
          setCounts(localCounts);
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveOrder = async (orderId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await ApiServices('put', `${ApiConfig.ORDERS_UPDATE_STATUS}/${orderId}/archive`);
      if (res?.success || res?.message === 'Order archived successfully') {
        message.success('Order archived');
        fetchOrders();
      } else {
        message.error(res?.message || 'Failed to archive order');
      }
    } catch (err) {
      message.error(err?.message || 'Failed to archive order');
    }
  };

  const handleUnarchiveOrder = async (orderId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await ApiServices('put', `${ApiConfig.ORDERS_UPDATE_STATUS}/${orderId}/unarchive`);
      if (res?.success || res?.message === 'Order restored from archive') {
        message.success('Order restored from archive');
        fetchOrders();
      } else {
        message.error(res?.message || 'Failed to restore order');
      }
    } catch (err) {
      message.error(err?.message || 'Failed to restore order');
    }
  };

  const handleDeleteOrder = (record, e) => {
    if (e) e.stopPropagation();
    Modal.confirm({
      title: 'Delete order permanently?',
      content: (
        <>
          Order <strong>{record.orderId}</strong> and all related data (shipments, items, addresses) will be deleted. This cannot be undone.
        </>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await ApiServices('delete', `${ApiConfig.ORDERS_UPDATE_STATUS}/${record.id}`);
          if (res?.success || res?.message?.toLowerCase().includes('deleted')) {
            message.success('Order deleted');
            fetchOrders();
          } else {
            message.error(res?.message || 'Failed to delete order');
          }
        } catch (err) {
          message.error(err?.message || 'Failed to delete order');
        }
      },
    });
  };

  const fetchWarehouses = async () => {
    try {
      const response = await ApiServices('get', ApiConfig.WAREHOUSE);
      setWarehouses(response?.data || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await ApiServices('get', driversListUrl(userDetail?.storeId));
      setDrivers(response?.data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const hasDriverAssigned = (order) => {
    const shipments = order.shipments || [];
    return shipments.some(s => s.driverId);
  };
  const isOrderComplete = (order) => ['DELIVERED', 'COMPLETED'].includes(order.orderStatus);

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse?.name || 'N/A';
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? `${driver.firstName} ${driver.lastName}` : 'Not Assigned';
  };

  const getDeliveryAddress = (order) => {
    const addresses = order.orderAddresses || order.OrderAddresses || [];
    const delivery = addresses.find(addr => addr.addressType === 'Delivery');
    if (!delivery) return 'N/A';
    return `${delivery.city || ''}, ${delivery.state || ''} ${delivery.zipcode || ''}`.trim();
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 140,
      fixed: 'left',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      sorter: (a, b) => String(a?.orderId || '').localeCompare(String(b?.orderId || ''), undefined, { numeric: true, sensitivity: 'base' }),
      sortDirections: ['ascend', 'descend'],
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <Text strong style={{ fontSize: 13 }}>{text || 'N/A'}</Text>
            {record.archivedAt && (
              <Tag color="default" style={{ fontSize: 10, padding: '0 4px', lineHeight: 1.4 }}>Archived</Tag>
            )}
            {record.platformType && (
              <Tag color="purple" style={{ fontSize: 10, padding: '0 4px', lineHeight: 1.4 }}>
                {String(record.platformType).charAt(0).toUpperCase() + record.platformType.slice(1)}
              </Tag>
            )}
          </Space>
          {((record.shipments || [])[0]?.trackingNumber || record.trackingNumber) && (
            <Text type="secondary" style={{ fontSize: 11 }}>Track: {(record.shipments || [])[0]?.trackingNumber || record.trackingNumber}</Text>
          )}
          {!record.trackingNumber && record.shopifyOrderGid && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.shopifyOrderGid.split('/').pop()}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 160,
      responsive: ['sm', 'md', 'lg', 'xl'],
      sorter: (a, b) => String(a?.customerName || '').localeCompare(String(b?.customerName || ''), undefined, { sensitivity: 'base' }),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
            <Text strong>{record.customerName || 'N/A'}</Text>
          </Space>
          {record.customerPhone && (
            <Space size={4}>
              <PhoneOutlined style={{ fontSize: 11, color: '#52c41a' }} />
              <Text type="secondary" style={{ fontSize: 12 }}>{record.customerPhone}</Text>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: 'Warehouse',
      key: 'warehouse',
      width: 120,
      responsive: ['md', 'lg', 'xl'],
      sorter: (a, b) => {
        const getWhId = (r) => ((r.shipments || []).find(s => s.pickupWarehouseId)?.pickupWarehouseId || r.warehouseId);
        return String(getWarehouseName(getWhId(a)) || '').localeCompare(String(getWarehouseName(getWhId(b)) || ''), undefined, { sensitivity: 'base' });
      },
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => {
        const shipment = (record.shipments || []).find(s => s.pickupWarehouseId);
        const whId = shipment?.pickupWarehouseId || record.warehouseId;
        return (
          <Space>
            <ShopOutlined style={{ color: '#722ed1' }} />
            <Text>{getWarehouseName(whId)}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Driver',
      key: 'driver',
      width: 120,
      responsive: ['sm', 'md', 'lg', 'xl'],
      sorter: (a, b) => {
        const getDriverId = (r) => ((r.shipments || []).find(s => s.driverId)?.driverId || r.driverId);
        return String(getDriverName(getDriverId(a)) || '').localeCompare(String(getDriverName(getDriverId(b)) || ''), undefined, { sensitivity: 'base' });
      },
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => {
        const shipment = (record.shipments || []).find(s => s.driverId);
        const driverId = shipment?.driverId || record.driverId;
        const canAssign = !record.archivedAt && !hasDriverAssigned(record);
        return (
          <Space>
            {driverId ? (
              <>
                <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#52c41a' }} />
                <Text>{getDriverName(driverId)}</Text>
              </>
            ) : (
              <Space size={6}>
                <Tag color="default">Not Assigned</Tag>
                {canAssign && (
                  <Tooltip title="Assign driver">
                    <Button
                      size="small"
                      type="text"
                      icon={<UserAddOutlined style={{ color: '#1677ff' }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(record);
                        setAsignDriverModalOpen(true);
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      responsive: ['sm', 'md', 'lg', 'xl'],
      sorter: (a, b) => {
        const sa = String(a?.orderStatus || a?.deliveryStatus || a?.status || 'PENDING');
        const sb = String(b?.orderStatus || b?.deliveryStatus || b?.status || 'PENDING');
        return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
      },
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => {
        const status = record.orderStatus || record.deliveryStatus || record.status || 'PENDING';
        const config = getStatusConfig(status);
        return (
          <Tag
            style={{
              padding: '3px 10px',
              borderRadius: 6,
              border: `1px solid ${config.color}`,
              backgroundColor: config.bg,
              color: config.textColor || config.color,
              fontWeight: 500,
              fontSize: 11,
            }}
            icon={config.icon}
          >
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Payment',
      key: 'payment',
      width: 100,
      responsive: ['md', 'lg', 'xl'],
      sorter: (a, b) => String(a?.paymentType || '').localeCompare(String(b?.paymentType || ''), undefined, { sensitivity: 'base' }),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => {
        const paymentType = record.paymentType || 'COD';
        const isCOD = paymentType === 'COD';
        return (
          <Space direction="vertical" size={0}>
            <Tag color={isCOD ? 'volcano' : 'green'}>{paymentType}</Tag>
            {isCOD && record.amount && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.currency_symbol || '₹'} {parseFloat(record.amount || 0).toFixed(2)}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Address',
      key: 'deliveryAddress',
      width: 140,
      responsive: ['lg', 'xl'],
      render: (_, record) => {
        const address = getDeliveryAddress(record);
        return (
          <Tooltip title={address}>
            <Space>
              <EnvironmentOutlined style={{ color: '#1890ff' }} />
              <Text ellipsis style={{ maxWidth: 150 }}>{address}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      responsive: ['md', 'lg', 'xl'],
      sorter: (a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime(),
      sortDirections: ['descend', 'ascend'],
      render: (date) => (
        <Space direction="vertical" size={0}>
          <Text>{moment(date).format('DD MMM YYYY')}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {moment(date).format('HH:mm A')}
          </Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      fixed: 'right',
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/Orders/${record.id}`)}
            />
          </Tooltip>
          <Dropdown
            overlay={
              <Menu>
                {!record.archivedAt && !hasDriverAssigned(record) && (
                  <Menu.Item 
                    key="assign-driver" 
                    icon={<UserOutlined />}
                    onClick={(e) => {
                      e.domEvent?.stopPropagation();
                      setSelectedOrder(record);
                      setAsignDriverModalOpen(true);
                    }}
                  >
                    Assign Driver
                  </Menu.Item>
                )}
                <Menu.Item 
                  key="view-map" 
                  icon={<EnvironmentOutlined />}
                  onClick={(e) => {
                    e.domEvent?.stopPropagation();
                    const addresses = record.orderAddresses || record.OrderAddresses || [];
                    const delivery = addresses.find(addr => addr.addressType === 'Delivery');
                    if (delivery?.latitude && delivery?.longitude) {
                      openMap(delivery.latitude, delivery.longitude);
                    }
                  }}
                >
                  View on Map
                </Menu.Item>
                {record.archivedAt ? (
                  <Menu.Item 
                    key="unarchive" 
                    icon={<RollbackOutlined />}
                    onClick={(e) => { e.domEvent?.stopPropagation(); handleUnarchiveOrder(record.id, e.domEvent); }}
                  >
                    Restore from archive
                  </Menu.Item>
                ) : (
                  <Menu.Item 
                    key="archive" 
                    icon={<InboxOutlined />}
                    onClick={(e) => { e.domEvent?.stopPropagation(); handleArchiveOrder(record.id, e.domEvent); }}
                  >
                    Archive order
                  </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item 
                  key="delete" 
                  icon={<DeleteOutlined />}
                  danger
                  onClick={(e) => { e.domEvent?.stopPropagation(); handleDeleteOrder(record, e.domEvent); }}
                >
                  Delete order
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
            onClick={(e) => e.stopPropagation()}
          >
            <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const statusFilters = [
    { value: 'all', label: 'All', count: counts.all || 0 },
    { value: 'PENDING', label: 'Pending', count: counts.PENDING || 0 },
    { value: 'WAITING_FOR_DRIVER_ACCEPT', label: 'Waiting for Driver Accept', count: counts.WAITING_FOR_DRIVER_ACCEPT || 0 },
    { value: 'READY_FOR_PICKUP', label: 'Ready', count: counts.READY_FOR_PICKUP || 0 },
    { value: 'PARTIAL_READY_TO_PICKUP', label: 'Partial Ready', count: counts.PARTIAL_READY_TO_PICKUP || 0 },
    { value: 'IN_TRANSIT', label: 'In Transit', count: counts.IN_TRANSIT || 0 },
    { value: 'PARTIAL_IN_TRANSIT', label: 'Partial In Transit', count: counts.PARTIAL_IN_TRANSIT || 0 },
    { value: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', count: counts.OUT_FOR_DELIVERY || 0 },
    { value: 'DELIVERED', label: 'Delivered', count: counts.DELIVERED || 0 },
    { value: 'PARTIAL_DELIVERED', label: 'Partial Delivered', count: counts.PARTIAL_DELIVERED || 0 },
    { value: 'COMPLETED', label: 'Completed', count: counts.COMPLETED || 0 },
    { value: 'CLOSED', label: 'Closed', count: counts.CLOSED || 0 },
    { value: 'CANCELLED', label: 'Cancelled', count: counts.CANCELLED || 0 },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.archivedAt || hasDriverAssigned(record),
    }),
  };

  // Context data for child components
  const contextValue = {
    asignDriverModalOpen,
    setAsignDriverModalOpen,
    submitAsignDriver: () => {},
    wareHouseData: warehouses,
    driverData: drivers,
    selectedOrder,
    activeButton,
    setActiveButton,
    getOrders: fetchOrders,
    getAssignOrders: () => {},
    multipleModalOpen,
    setMultipleModalOpen,
    awaitingOrders: orders.filter((order) => !hasDriverAssigned(order)),
    selectedRowKeys,
    setSelectedRowKeys,
    orders,
    userDetail,
  };

  return (
    <HomeContext.Provider value={contextValue}>
      <MainLayout>
        <div className={styles.page}>
          <div className={styles.topBar}>
            <Title level={4} className={styles.pageTitle}>Orders</Title>
            <Space size={8}>
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchOrders}>Refresh</Button>
              {selectedRowKeys.length > 0 && (
                <Button size="small" type="primary" icon={<ExportOutlined />} onClick={() => setMultipleModalOpen(true)}>
                  Bulk ({selectedRowKeys.length})
                </Button>
              )}
            </Space>
          </div>

          <DashboardCards
            orders={counts.all || 0}
            todayOrders={counts.today || 0}
            assignedOrders={counts.assigned || 0}
            CompleteOrder={counts.completed || 0}
          />

          <Card className={styles.filterCard}>
            <Row gutter={12} align="middle">
              <Col flex="auto">
                <Search
                  placeholder="Order ID, Customer, Phone, Email, Tracking"
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="middle"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ maxWidth: 360 }}
                />
              </Col>
              <Col>
                <Select
                  value={archiveFilter}
                  onChange={setArchiveFilter}
                  style={{ width: 130 }}
                  size="middle"
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'archived', label: 'Archived' },
                    { value: 'all', label: 'All' },
                  ]}
                />
              </Col>
              <Col>
                <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }} size="middle">
                  {statusFilters.map(f => (
                    <Option key={f.value} value={f.value}>{f.label} ({f.count})</Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <RangePicker size="middle" value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" />
              </Col>
            </Row>
            <div style={{ marginTop: 12 }}>
              <SegmentedButton activeButton={activeButton} setActiveButton={setActiveButton} />
            </div>
          </Card>

          <Card className={styles.tableCard}>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={orders}
            loading={loading}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalOrders,
              showSizeChanger: true,
              showTotal: (total) => `${total} orders`,
              size: 'small',
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              }
            }}
            onRow={(record) => ({
              onClick: () => navigate(`/Orders/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
          </Card>

          <AsignDriver />
        <MultipleOrdersAssign 
          open={multipleModalOpen}
          onClose={() => { setMultipleModalOpen(false); setSelectedRowKeys([]); }}
          selectedRowKeys={selectedRowKeys}
          onSuccess={fetchOrders}
        />
        </div>
      </MainLayout>
    </HomeContext.Provider>
  );
};

export default Orders;
